import * as React from "react";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { Hash, Bot, LayoutGrid, ChevronRight } from "lucide-react";
import { getPersonaTheme } from "./personaTheme";

interface Pane1Props {
  channels: any[];
  activeThreadId?: string;
  activeThread?: any;
  activeView: "chat" | "agents";
  onSelectView: (view: "chat" | "agents") => void;
  onSelectThread: (threadId: string) => void;
  onSelectAgentDM: (personaKey: string) => void;
  pendingApprovalsCount: number;
}

export function Pane1AgentsChannels({
  channels,
  activeThreadId,
  activeThread,
  activeView,
  onSelectView,
  onSelectThread,
  onSelectAgentDM,
  pendingApprovalsCount,
}: Pane1Props) {
  const chiefOfStaff = WORKFORCE_PERSONAS.chief_of_staff;
  const specialistPersonas = Object.values(WORKFORCE_PERSONAS).filter(
    (p) => p.key !== "chief_of_staff"
  );

  const isAlexActive =
    activeView === "chat" &&
    (activeThread?.channelSlug === "dm-chief_of_staff" ||
      (activeThread?.type === "dm" && activeThread?.participantPersonas?.[0] === "chief_of_staff"));

  return (
    <div className="w-full h-full min-h-0 min-w-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden select-none">
      {/* Header & Navigation */}
      <div className="p-3.5 border-b border-gray-100 bg-white flex-shrink-0">
        {/* Team Guide Navigation Button */}
        <button
          onClick={() => onSelectView("agents")}
          className={`w-full text-left px-3.5 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-between transition-all border shadow-2xs ${
            activeView === "agents"
              ? "bg-blue-600 text-white border-blue-600 shadow-xs"
              : "bg-slate-50 text-slate-800 border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <LayoutGrid className={`w-4 h-4 ${activeView === "agents" ? "text-white" : "text-blue-600"}`} />
            <span>Team Guide & Routines</span>
          </div>
          <ChevronRight className={`w-4 h-4 ${activeView === "agents" ? "text-white" : "text-gray-400"}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3.5 space-y-5 custom-scrollbar min-w-0">
        {/* Channels */}
        <div>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-400 px-1 mb-2">
            <span>Channels</span>
            {pendingApprovalsCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold border border-amber-200" title="Pending approvals">
                {pendingApprovalsCount} pending
              </span>
            )}
          </div>
          <div className="space-y-1">
            {channels.map((ch) => {
              const isActive = ch._id === activeThreadId;
              return (
                <button
                  key={ch._id}
                  onClick={() => {
                    onSelectView("chat");
                    onSelectThread(ch._id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2.5 transition-all min-w-0 ${
                    isActive && activeView === "chat"
                      ? "bg-blue-50 text-blue-700 font-semibold border border-blue-200 shadow-xs"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 border border-transparent"
                  }`}
                >
                  <Hash className={`w-4 h-4 flex-shrink-0 ${isActive && activeView === "chat" ? "text-blue-600" : "text-gray-400"}`} />
                  <span className="truncate">{ch.title.replace("# ", "")}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Operations Team */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-gray-400 px-1 mb-2">
            Operations Team
          </div>

          {/* Alex - Chief of Operations */}
          <div className="mb-2">
            <button
              onClick={() => onSelectAgentDM(chiefOfStaff.key)}
              className={`w-full group text-left p-3 rounded-xl transition-all flex items-center gap-3 min-w-0 ${
                isAlexActive
                  ? "bg-blue-50 border-2 border-blue-600 shadow-xs"
                  : "bg-gradient-to-r from-blue-50/80 to-indigo-50/60 border border-blue-200 hover:border-blue-300 hover:shadow-xs"
              }`}
            >
              <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-2xs">
                <Bot className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors truncate">
                    {chiefOfStaff.name}
                  </span>
                  <span className="text-xs text-blue-700 font-semibold bg-blue-100 px-2 py-0.5 rounded-md">
                    Lead
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate font-medium mt-0.5">
                  Chief of Operations
                </p>
              </div>
            </button>
          </div>

          {/* Specialized Team Members */}
          <div className="space-y-1">
            {specialistPersonas.map((persona) => {
              const theme = getPersonaTheme(persona.key);
              const isPersonaActive =
                activeView === "chat" &&
                (activeThread?.channelSlug === `dm-${persona.key}` ||
                  (activeThread?.type === "dm" && activeThread?.participantPersonas?.[0] === persona.key));

              return (
                <button
                  key={persona.key}
                  onClick={() => onSelectAgentDM(persona.key)}
                  className={`w-full text-left px-2.5 py-2 rounded-xl border transition-all flex items-center justify-between group min-w-0 ${
                    isPersonaActive
                      ? `${theme.bgLight} border-2 ${theme.border} shadow-xs`
                      : "border-transparent hover:border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-lg ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-2xs`}
                    >
                      <Bot className="w-4 h-4" />
                    </div>
                    <div className="truncate">
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors block truncate">
                        {persona.name}
                      </span>
                      <span className="text-xs text-gray-500 block truncate font-medium mt-0.5">
                        {persona.title.replace(" Agent", "")}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
